// @flow
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Play } from '@finect/tabular-icons/play';
import { Link } from '@finect/tabular-icons/link';
import { DateLabel } from '@finect/tabular-components/DateLabel';
import { Icon } from '@finect/tabular-components/Icon';
import { Image } from '@finect/tabular-components/Image';
import { EntityCard } from '@finect/tabular-components/EntityCard';
import { FinectIsoPlus } from '@finect/tabular-components/FinectIsoPlus';
import { PlusLogo } from '@finect/tabular-components/PlusLogo';
import { normalizeVideo } from '@finect/tabular-components/Articles';
import {
  getArticlePath,
  getArticleRel,
  getArticleDek,
  getReadingMinutes,
  getArticlePublishedDate,
  getMediaDurationSeconds,
  getMediaMinutes,
  getMediaThumbUrl,
  formatMediaDuration,
  getRelativeTimeAgo,
  pickArticleAuthor,
  getAuthorProfilePath
} from '../../../utils/articles';
import { SITE_TIME_ZONE } from '../../../utils/siteTimeZone';
import {
  FeedRow,
  TimeCell,
  FeedRowContent,
  Kicker,
  KickerProfessional,
  KickerSeparator,
  PlusChip,
  PlusChipButton,
  FeedHeadline,
  HeadlineLink,
  Dek,
  MetaRow,
  MetaAvatar,
  MetaName,
  MetaSeparator,
  ExternalSource,
  ThumbCell,
  TypeBadge
} from './partials';

// Rango de ids de los temas que marcan contenido Plus (mismo criterio que ArticleListCard).
const PLUS_TAG_MIN = 50100;
const PLUS_TAG_MAX = 50110;

// Por debajo de una hora la hora de reloj aporta menos que el tiempo transcurrido: en un feed de
// última hora lo que importa es "acaba de salir", no si fueron las 14:32 o las 14:36. Marca además
// la fila como recién publicada, con el color de marca y negrita.
const FRESH_THRESHOLD_MS = 60 * 60 * 1000;

// Tamaño en CSS de la miniatura. Image aplica factor 2 al construir la URL, así que estos son
// los valores que van también en los atributos width/height del img y evitan el salto de layout.
const THUMB_IMAGE_WIDTH = 260;
const THUMB_IMAGE_HEIGHT = 163;

const isPlusTag = tag => tag.id >= PLUS_TAG_MIN && tag.id <= PLUS_TAG_MAX;

const TYPE_BADGES = {
  video: 'Vídeo',
  podcast: 'Podcast'
};

type Props = {
  article: Object,
  userSubscriptions?: Object,
  withAuthor?: boolean,
  // Marca de tiempo del cliente. Llega desde la lista, que la calcula una sola vez al montar:
  // decidir aquí el "ahora" rompería la hidratación, porque el servidor y el navegador
  // renderizarían textos distintos. Con null no hay tiempo relativo y se pinta la fecha o la hora.
  nowTs?: number | null,
  imageLoading?: 'lazy' | 'eager',
  imageFetchPriority?: 'high' | 'low' | 'auto',
  // Qué se pinta cuando NO hay tiempo relativo, o sea en el render de servidor: con `true` la
  // fecha, y sin él la hora de reloj. El feed de noticias puede quedarse con la hora porque sus
  // cabeceras de día ya dicen el día; una lista sin esas cabeceras dejaría "00:00h" en una pieza
  // de 2023, así que pide la fecha.
  withDate?: boolean
};

/**
 * Fila de artículo del feed de noticias.
 *
 * Jerarquía en cuatro niveles —antetítulo, titular, entradilla y firma— con la hora de
 * publicación en columna propia a la izquierda y la miniatura a la derecha. Todas las filas son
 * idénticas a propósito: el feed es infinito y está ordenado por hora, así que si el tamaño
 * variara el lector lo leería como importancia cuando solo indicaría posición en la lista. La
 * estructura temporal la aporta la cabecera de día del feed, no la card.
 *
 * @param article Artículo del modelo de contenidos
 * @param userSubscriptions Suscripciones del usuario, para saber si sigue el tema
 * @param withAuthor Muestra la firma del autor
 * @param nowTs Marca de tiempo del cliente para calcular el tiempo transcurrido
 * @param imageLoading Estrategia de carga de la miniatura
 * @param imageFetchPriority Prioridad de descarga de la miniatura
 */
const ArticleFeedCardView = ({
  article,
  userSubscriptions = {},
  withAuthor = true,
  nowTs = null,
  imageLoading = 'lazy',
  imageFetchPriority,
  withDate = false
}: Props) => {
  const dispatch = useDispatch();
  const user = useSelector(state => state.user.user);
  const userIsPlus = Boolean(user?.plus);

  const { title } = article;
  const tags = article.socialStats?.tags || [];
  const articleIsPlus = tags.some(isPlusTag);
  const isMedia = article.type === 'video' || article.type === 'podcast';

  const link = getArticlePath(article);
  const rel = getArticleRel(article);
  const externalUrl = article.body?.url || '';
  const [urlHost] = (externalUrl.match('^(?:https?://)?(?:[^@\n]+@)?(?:www.)?([^:/\n?]+)') || []).slice(1);

  // En un vídeo o un podcast la url del body es el propio embed, no la fuente de la que se informa,
  // así que anunciar "youtube.com" en cada fila no dice nada y lo repite todo el listado.
  const externalHost = isMedia ? null : urlHost;

  // El titular apunta a la ficha del artículo incluso cuando hay URL externa: es la página
  // indexable y desde ella se enlaza a la fuente. El dominio de origen va aparte, en la firma.
  const headlineHref = link;

  const date = getArticlePublishedDate(article);
  const dateObject = date ? new Date(date) : null;
  const elapsedMs = nowTs && dateObject ? nowTs - dateObject.getTime() : null;
  const isFresh = elapsedMs !== null && elapsedMs >= 0 && elapsedMs < FRESH_THRESHOLD_MS;

  // Un artículo Plus solo muestra entradilla a quien puede leerlo; al resto se le enseña el
  // titular y el chip, que es lo que le invita a suscribirse.
  const dek = articleIsPlus && !userIsPlus ? '' : getArticleDek(article);

  const author = withAuthor ? pickArticleAuthor(article) : null;
  const profileHref = getAuthorProfilePath(author);
  const [organization] = author?.organizations || [];

  // Solo para saber si hay un vídeo reconocible detrás de la URL, que es lo que decide el badge.
  const hasVideo = article.type === 'video' && Boolean(normalizeVideo(externalUrl)?.id);

  // En medios se cae a la carátula de YouTube cuando el artículo no tiene imagen propia; en el
  // resto sigue siendo la imagen del artículo o nada.
  const image = isMedia
    ? getMediaThumbUrl(article, THUMB_IMAGE_WIDTH)
    : article.image && getMediaThumbUrl(article, THUMB_IMAGE_WIDTH);

  // El badge dice la duración cuando la sabemos y el tipo cuando no. La duracion informa mas y
  // ocupa menos: "9:04" frente a "Video", y ya se entiende que es reproducible por el icono.
  const durationLabel = formatMediaDuration(getMediaDurationSeconds(article));
  const typeBadge = article.type === 'video' && !hasVideo ? null : TYPE_BADGES[article.type];
  const badge = durationLabel || typeBadge;

  // El tema del antetítulo es el primero que no sea de Plus: los de Plus ya se comunican con el
  // chip, y repetirlos gastaría el único hueco de categoría que tiene la fila.
  const [topic] = tags.filter(tag => !isPlusTag(tag));
  const topicFollowed = Boolean(topic && userSubscriptions?.tags?.includes(topic.id));

  const handlePlusChipClick = (event) => {
    event.preventDefault();
    dispatch({ type: 'SET_PLUS_MODAL', open: true });
  };

  // El sello de tiempo se resuelve fuera del JSX: en la fila solo queda el dato ya elegido.
  //
  // La escala relativa completa ("hace 3 semanas", "hace 2 meses") es SOLO para vídeos y podcasts:
  // viven en listados sin cabecera de día, así que sin ella no se sabría de cuándo son. Un artículo
  // normal se queda como estaba —la hora de reloj, y el tiempo transcurrido en la primera hora—
  // porque su feed ya trae un separador de día por encima que dice el día.
  //
  // En el render de servidor no hay reloj de cliente (nowTs es null) y no hay tiempo relativo: se
  // pinta la hora o la fecha, que es lo que el primer render del navegador tiene que reproducir
  // para que la hidratación cuadre. En cuanto monta, pasa a relativo. Las dos en la zona de Finect:
  // el servidor está en UTC y el navegador, en la del lector (ver `SITE_TIME_ZONE`).
  const clockLabel = dateObject ? (
    <DateLabel date={dateObject} format={withDate ? 'll' : 'S'} timeZone={SITE_TIME_ZONE} />
  ) : null;

  // La misma función cubre los dos casos: para un artículo recién publicado devuelve "hace 12 min",
  // que es exactamente lo que se pintaba antes.
  //
  // La comprobación de `elapsedMs` va aquí y no en un booleano aparte porque Flow no refina a
  // través de él: con `hasElapsed && getRelativeTimeAgo(elapsedMs)` el null sigue en el tipo.
  const timeLabel =
    elapsedMs !== null && elapsedMs >= 0 && (isMedia || isFresh) ? getRelativeTimeAgo(elapsedMs) : clockLabel;

  // En un vídeo o un podcast el cuerpo es el embed y un párrafo, asi que "1 min de lectura" no
  // describe nada: lo que dura es el medio. Si no sabemos la duración, no se dice nada.
  const mediaMinutes = isMedia ? getMediaMinutes(article) : 0;
  const readingMinutes = isMedia ? 0 : getReadingMinutes(article);
  const durationMeta = mediaMinutes
    ? `${mediaMinutes} min de ${article.type === 'podcast' ? 'audio' : 'vídeo'}`
    : readingMinutes > 0 && `${readingMinutes} min de lectura`;

  const plusChip = (
    <PlusChip>
      <FinectIsoPlus color="#fff" size={12} aria-hidden="true" />
      <PlusLogo color="#fff" size={22} aria-hidden="true" />
    </PlusChip>
  );

  return (
    <FeedRow plus={articleIsPlus} withImage={Boolean(image)} data-testid={`feed-article-${article.id}`}>
      <TimeCell fresh={isFresh}>{timeLabel}</TimeCell>

      <FeedRowContent>
        {(articleIsPlus || topic || article.forProfessionals) && (
          <Kicker>
            {articleIsPlus &&
              (userIsPlus ? (
                <a href="/plus/analisis" rel="nofollow" title="Contenido Plus">
                  {plusChip}
                </a>
              ) : (
                <PlusChipButton type="button" onClick={handlePlusChipClick} aria-label="Contenido Plus: ver suscripción">
                  {plusChip}
                </PlusChipButton>
              ))}

            {articleIsPlus && topic && <KickerSeparator aria-hidden="true">|</KickerSeparator>}

            {topic && (
              <EntityCard type="tag" entity={topic}>
                <a
                  href={`/temas/${topic.alias ? topic.alias : topic.url}`}
                  title={topicFollowed ? `${topic.name} (sigues este tema)` : topic.name}
                >
                  {topic.name}
                </a>
              </EntityCard>
            )}

            {article.forProfessionals && (
              <>
                {(articleIsPlus || topic) && <KickerSeparator aria-hidden="true">|</KickerSeparator>}
                <KickerProfessional>Profesionales</KickerProfessional>
              </>
            )}
          </Kicker>
        )}

        <FeedHeadline>
          <HeadlineLink href={headlineHref} title={title} {...(rel ? { rel } : {})} data-fi-event="feed-article-card">
            {title}
          </HeadlineLink>
        </FeedHeadline>

        {dek && <Dek>{dek}</Dek>}

        <MetaRow>
          {author && (
            <>
              <MetaAvatar>
                <Icon
                  withBorder
                  size={-1}
                  type={author.subtype === 'company' ? 'company' : 'user'}
                  alt={author.displayName}
                  image={author.icon ? `/image/${author.icon}` : ''}
                />
              </MetaAvatar>
              {/* El mismo componente que en la cabecera del articulo y en las tarjetas de vídeo: al
                  pasar el ratón sale la ficha del autor, y el enlace al perfil quita el cursor de
                  texto que dejaba el <span>. */}
              <EntityCard type="profile" entity={{ ...author, name: author.displayName }}>
                <MetaName
                  {...profileHref ? { href: profileHref, title: `Ver el perfil de ${author.displayName}` } : { as: 'span' }}
                >
                  {author.displayName}
                </MetaName>
              </EntityCard>
            </>
          )}

          {author && organization && (
            <>
              <MetaSeparator aria-hidden="true">&bull;</MetaSeparator>
              <span>{organization.displayName}</span>
            </>
          )}

          {externalHost && (
            <>
              {author && <MetaSeparator aria-hidden="true">&bull;</MetaSeparator>}
              <ExternalSource
                href={externalUrl}
                rel="nofollow"
                target="_blank"
                title={`Abrir la fuente original en ${externalHost}`}
              >
                <Link size="12" strokeWidth="2.2" aria-hidden="true" />
                <span>{externalHost}</span>
              </ExternalSource>
            </>
          )}

          {durationMeta && (
            <>
              {(author || externalHost) && <MetaSeparator aria-hidden="true">&bull;</MetaSeparator>}
              <span>{durationMeta}</span>
            </>
          )}
        </MetaRow>
      </FeedRowContent>

      {image && (
        <ThumbCell>
          <Image
            src={image}
            alt={title}
            width={THUMB_IMAGE_WIDTH}
            height={THUMB_IMAGE_HEIGHT}
            loading={imageLoading}
            fetchPriority={imageFetchPriority}
          />
          {badge && (
            <TypeBadge>
              <Play size="10" strokeWidth="2" color="#fff" aria-hidden="true" />
              {badge}
            </TypeBadge>
          )}
        </ThumbCell>
      )}
    </FeedRow>
  );
};

/**
 * El feed vuelve a pintar la lista entera cada vez que cambia el día que se está leyendo, que es en
 * cada tramo al hacer scroll. Sin memo, eso repintaba las N filas acumuladas del scroll infinito.
 * Requiere que el feed pase props estables: userSubscriptions memoizado y los callbacks constantes.
 */
export const ArticleFeedCard: any = React.memo(ArticleFeedCardView);

ArticleFeedCard.displayName = 'ArticleFeedCard';
